import React, { useEffect } from 'react';
import { Link } from 'react-router-dom';

/*
  About.jsx — Flap Technology
  ─────────────────────────────────────────────────────────────────────────────
  Based on:
  - Ward 26 Smart School Attendance & Identity Management System proposal
    (Pokhara Metropolitan City, Fiscal Year 2082/83 / 2025)
  - Submitted by: Bikalpa Dhungana, Flap Technology Startup
  - Nepal's first NFC smart product company
  - Prime Minister National Talent Award recipient
  - 7 schools, 1,700 students in Pokhara Ward No. 26
  - Contact: bikalpa@flap.com.np | flap.com.np

  Theme: #1c73ba blue system consistent with rest of app
───────────────────────────────────────────────────────────────────────────── */

const CSS = `
@import url('https://fonts.googleapis.com/css2?family=DM+Sans:ital,opsz,wght@0,9..40,300;0,9..40,400;0,9..40,500;0,9..40,600;0,9..40,700;1,9..40,400&display=swap');
@import url('https://api.fontshare.com/v2/css?f[]=clash-display@400,500,600,700&display=swap');

.ab-root {
  font-family: 'DM Sans', system-ui, sans-serif;
  background: #fff;
  color: #1e293b;
  overflow-x: hidden;
  -webkit-font-smoothing: antialiased;
}

/* ── Tokens ── */
.ab-root {
  --ab-blue:   #1c73ba;
  --ab-blue2:  #2b8de0;
  --ab-bl:     #e8f3fb;
  --ab-navy:   #06112b;
  --ab-navy2:  #0d1f45;
  --ab-gold:   #f5a623;
  --ab-muted:  #6b7280;
  --ab-border: rgba(28,115,186,0.13);
  --ab-bg:     #f8fafd;
}

/* ── HERO ─────────────────────────────────────────────────────────────────── */
.ab-hero {
  background: linear-gradient(145deg, var(--ab-navy) 0%, var(--ab-navy2) 60%, #0f2d5c 100%);
  padding: 96px 6% 80px;
  position: relative; overflow: hidden;
  text-align: center;
}
.ab-hero::before {
  content: ''; position: absolute; inset: 0;
  background-image:
    linear-gradient(rgba(28,115,186,0.07) 1px, transparent 1px),
    linear-gradient(90deg, rgba(28,115,186,0.07) 1px, transparent 1px);
  background-size: 52px 52px;
}
.ab-hero::after {
  content: ''; position: absolute;
  width: 700px; height: 700px;
  background: radial-gradient(circle, rgba(28,115,186,0.18) 0%, transparent 70%);
  top: 50%; left: 50%; transform: translate(-50%, -50%);
}
.ab-hero-inner { max-width: 800px; margin: 0 auto; position: relative; z-index: 1; }
.ab-eyebrow {
  display: inline-flex; align-items: center; gap: 7px;
  background: rgba(28,115,186,0.18); border: 1px solid rgba(28,115,186,0.35);
  border-radius: 99px; padding: 5px 14px; margin-bottom: 24px;
  font-size: 0.76rem; font-weight: 700; color: #93c5fd;
  letter-spacing: 1.8px; text-transform: uppercase;
}
.ab-hero h1 {
  font-family: 'Clash Display', sans-serif;
  font-size: clamp(2.4rem, 5vw, 3.6rem);
  font-weight: 700; color: #fff;
  line-height: 1.1; margin-bottom: 20px;
}
.ab-hero h1 em { font-style: normal; color: #93c5fd; }
.ab-hero h1 strong {
  background: linear-gradient(90deg, var(--ab-gold), #ffc94d);
  -webkit-background-clip: text; -webkit-text-fill-color: transparent;
  background-clip: text;
}
.ab-hero-lead {
  font-size: 1.05rem; color: rgba(255,255,255,0.65); max-width: 640px;
  margin: 0 auto 40px; line-height: 1.75;
}
.ab-hero-award {
  display: inline-flex; align-items: center; gap: 10px;
  background: rgba(245,166,35,0.1); border: 1px solid rgba(245,166,35,0.3);
  border-radius: 12px; padding: 12px 20px; font-size: 14px;
  color: var(--ab-gold); font-weight: 600;
}

/* ── SECTION ─────────────────────────────────────────────────────────────── */
.ab-section { padding: 80px 6%; max-width: 1100px; margin: 0 auto; }
.ab-section.full-width { max-width: 100%; padding: 0; }

.ab-label {
  font-size: 0.72rem; font-weight: 700; letter-spacing: 2.5px;
  text-transform: uppercase; color: var(--ab-blue);
  display: block; margin-bottom: 12px;
}
.ab-h2 {
  font-family: 'Clash Display', sans-serif;
  font-size: clamp(1.8rem, 3vw, 2.6rem);
  font-weight: 700; color: var(--ab-navy); line-height: 1.2;
  margin-bottom: 20px;
}
.ab-h2 em { font-style: normal; color: var(--ab-blue); }
.ab-lead {
  font-size: 1rem; color: var(--ab-muted); line-height: 1.8;
  max-width: 640px; margin-bottom: 32px;
}

/* ── FOUNDER ─────────────────────────────────────────────────────────────── */
.ab-founder-section { padding: 80px 6%; background: var(--ab-bg); }
.ab-founder-inner { max-width: 1100px; margin: 0 auto; display: grid; grid-template-columns: 1fr 1fr; gap: 72px; align-items: center; }
.ab-founder-avatar {
  width: 140px; height: 140px; border-radius: 50%;
  background: linear-gradient(135deg, var(--ab-blue), #155fa0);
  display: flex; align-items: center; justify-content: center;
  font-family: 'Clash Display', sans-serif;
  font-size: 3.5rem; font-weight: 700; color: #fff;
  margin-bottom: 24px;
  box-shadow: 0 0 0 6px rgba(28,115,186,0.15), 0 12px 32px rgba(28,115,186,0.25);
}
.ab-founder-name {
  font-family: 'Clash Display', sans-serif; font-size: 1.8rem; font-weight: 700;
  color: var(--ab-navy); margin-bottom: 4px;
}
.ab-founder-role { font-size: 14px; color: var(--ab-blue); font-weight: 600; margin-bottom: 16px; }
.ab-founder-bio {
  font-size: 0.95rem; color: #4b5563; line-height: 1.8;
  margin-bottom: 24px;
}
.ab-contact-row { display: flex; flex-direction: column; gap: 10px; }
.ab-contact-link {
  display: inline-flex; align-items: center; gap: 8px;
  font-size: 13px; color: var(--ab-blue); text-decoration: none;
  font-weight: 600; transition: color 0.15s;
}
.ab-contact-link:hover { color: #155fa0; }
.ab-founder-visual { display: flex; flex-direction: column; gap: 14px; }
.ab-award-card {
  background: #fff; border-radius: 16px; padding: 22px 24px;
  border: 1px solid var(--ab-border);
  box-shadow: 0 2px 12px rgba(28,115,186,0.07);
  display: flex; align-items: flex-start; gap: 16px;
  transition: transform 0.2s;
}
.ab-award-card:hover { transform: translateY(-2px); }
.ab-award-icon {
  width: 44px; height: 44px; border-radius: 12px; flex-shrink: 0;
  background: linear-gradient(135deg, rgba(28,115,186,0.1), rgba(28,115,186,0.05));
  display: flex; align-items: center; justify-content: center; font-size: 22px;
}
.ab-award-title { font-size: 13px; font-weight: 700; color: var(--ab-navy); margin-bottom: 4px; }
.ab-award-desc  { font-size: 12px; color: var(--ab-muted); line-height: 1.6; }

/* ── STORY ─────────────────────────────────────────────────────────────────── */
.ab-story-section { padding: 80px 6%; }
.ab-story-inner { max-width: 1100px; margin: 0 auto; display: grid; grid-template-columns: 1fr 1.5fr; gap: 72px; align-items: start; }
.ab-story-prose { font-size: 0.97rem; color: #374151; line-height: 1.85; }
.ab-story-prose p { margin-bottom: 20px; }
.ab-story-prose p:last-child { margin-bottom: 0; }
.ab-story-prose strong { color: var(--ab-navy); font-weight: 700; }
.ab-story-prose a { color: var(--ab-blue); text-decoration: none; font-weight: 600; }

/* ── WARD 26 PROJECT ───────────────────────────────────────────────────────── */
.ab-ward-section {
  background: linear-gradient(135deg, var(--ab-blue) 0%, #155fa0 100%);
  padding: 80px 6%; position: relative; overflow: hidden;
}
.ab-ward-section::before {
  content: ''; position: absolute; inset: 0;
  background-image:
    linear-gradient(rgba(255,255,255,0.05) 1px, transparent 1px),
    linear-gradient(90deg, rgba(255,255,255,0.05) 1px, transparent 1px);
  background-size: 40px 40px;
}
.ab-ward-inner { max-width: 1100px; margin: 0 auto; position: relative; }
.ab-ward-grid { display: grid; grid-template-columns: 1fr 1fr; gap: 64px; align-items: start; }
.ab-ward-text .ab-label { color: rgba(255,255,255,0.65); }
.ab-ward-text .ab-h2 { color: #fff; }
.ab-ward-text .ab-h2 em { color: #93c5fd; }
.ab-ward-text .ab-lead { color: rgba(255,255,255,0.72); }
.ab-ward-stats { display: grid; grid-template-columns: 1fr 1fr; gap: 16px; }
.ab-ward-stat {
  background: rgba(255,255,255,0.12); border: 1px solid rgba(255,255,255,0.2);
  border-radius: 14px; padding: 20px 18px; backdrop-filter: blur(8px);
}
.ab-ward-stat-num {
  font-family: 'Clash Display', sans-serif; font-size: 2rem; font-weight: 700;
  color: #fff; line-height: 1; margin-bottom: 5px;
}
.ab-ward-stat-label {
  font-size: 11px; color: rgba(255,255,255,0.65); text-transform: uppercase;
  letter-spacing: 1.5px; font-weight: 600;
}
.ab-ward-features { margin-top: 28px; display: flex; flex-direction: column; gap: 12px; }
.ab-ward-feature {
  background: rgba(255,255,255,0.1); border: 1px solid rgba(255,255,255,0.18);
  border-radius: 11px; padding: 14px 16px;
  display: flex; align-items: flex-start; gap: 12px; font-size: 13px;
  color: rgba(255,255,255,0.88); line-height: 1.6;
}
.ab-ward-feature-icon { font-size: 18px; flex-shrink: 0; margin-top: 1px; }

/* ── WHY FLAP ─────────────────────────────────────────────────────────────── */
.ab-why-section { padding: 80px 6%; background: var(--ab-bg); }
.ab-why-inner { max-width: 1100px; margin: 0 auto; }
.ab-why-grid { display: grid; grid-template-columns: repeat(3,1fr); gap: 22px; margin-top: 48px; }
.ab-why-card {
  background: #fff; border-radius: 16px; padding: 28px 24px;
  border: 1px solid var(--ab-border);
  transition: transform 0.2s, box-shadow 0.2s;
  position: relative; overflow: hidden;
}
.ab-why-card::before {
  content: ''; position: absolute; top: 0; left: 0; right: 0; height: 3px;
  background: linear-gradient(90deg, var(--ab-blue), #5aaef7);
  transform: scaleX(0); transform-origin: left; transition: transform 0.25s;
}
.ab-why-card:hover { transform: translateY(-4px); box-shadow: 0 12px 36px rgba(28,115,186,0.1); }
.ab-why-card:hover::before { transform: scaleX(1); }
.ab-why-icon { font-size: 1.8rem; margin-bottom: 14px; }
.ab-why-title { font-family: 'Clash Display', sans-serif; font-size: 1.05rem; font-weight: 600; color: var(--ab-navy); margin-bottom: 9px; }
.ab-why-desc { font-size: 0.87rem; color: #4b5563; line-height: 1.65; }

/* ── TIMELINE ─────────────────────────────────────────────────────────────── */
.ab-timeline-section { padding: 80px 6%; }
.ab-timeline-inner { max-width: 800px; margin: 0 auto; }
.ab-timeline { position: relative; margin-top: 48px; }
.ab-timeline::before {
  content: ''; position: absolute; left: 18px; top: 0; bottom: 0;
  width: 2px; background: linear-gradient(to bottom, var(--ab-blue), rgba(28,115,186,0.15));
}
.ab-tl-item { display: flex; gap: 28px; margin-bottom: 32px; position: relative; }
.ab-tl-dot {
  width: 38px; height: 38px; border-radius: 50%; flex-shrink: 0; margin-top: 3px;
  background: var(--ab-blue); display: flex; align-items: center; justify-content: center;
  font-size: 15px; box-shadow: 0 0 0 5px rgba(28,115,186,0.12), 0 0 0 8px var(--ab-bg);
  position: relative; z-index: 1;
}
.ab-tl-content {
  background: #fff; border: 1px solid var(--ab-border); border-radius: 14px;
  padding: 18px 20px; flex: 1;
  transition: box-shadow 0.2s;
}
.ab-tl-content:hover { box-shadow: 0 4px 20px rgba(28,115,186,0.08); }
.ab-tl-time { font-size: 11px; font-weight: 700; color: var(--ab-blue); text-transform: uppercase; letter-spacing: 1.2px; margin-bottom: 5px; }
.ab-tl-title { font-family: 'Clash Display', sans-serif; font-size: 1rem; font-weight: 700; color: var(--ab-navy); margin-bottom: 7px; }
.ab-tl-desc { font-size: 13px; color: #4b5563; line-height: 1.65; }

/* ── TECH STACK ─────────────────────────────────────────────────────────────── */
.ab-tech-section { padding: 72px 6%; background: var(--ab-bg); }
.ab-tech-inner { max-width: 1100px; margin: 0 auto; }
.ab-tech-grid { display: grid; grid-template-columns: repeat(4,1fr); gap: 16px; margin-top: 40px; }
.ab-tech-item {
  background: #fff; border-radius: 14px; padding: 22px 18px;
  border: 1px solid var(--ab-border); text-align: center;
  transition: transform 0.2s;
}
.ab-tech-item:hover { transform: translateY(-3px); }
.ab-tech-icon { font-size: 2rem; margin-bottom: 10px; }
.ab-tech-name { font-size: 13px; font-weight: 700; color: var(--ab-navy); margin-bottom: 4px; }
.ab-tech-desc { font-size: 11px; color: var(--ab-muted); line-height: 1.5; }

/* ── CTA ─────────────────────────────────────────────────────────────────── */
.ab-cta {
  background: var(--ab-navy);
  padding: 80px 6%; text-align: center; position: relative; overflow: hidden;
}
.ab-cta::before {
  content: ''; position: absolute; width: 600px; height: 600px;
  background: radial-gradient(circle, rgba(28,115,186,0.15) 0%, transparent 70%);
  top: 50%; left: 50%; transform: translate(-50%, -50%);
}
.ab-cta h2 {
  font-family: 'Clash Display', sans-serif;
  font-size: clamp(1.8rem, 3.5vw, 2.6rem); font-weight: 700;
  color: #fff; position: relative; margin-bottom: 14px;
}
.ab-cta p { font-size: 1rem; color: rgba(255,255,255,0.6); margin-bottom: 32px; position: relative; }
.ab-cta-btns { display: flex; gap: 12px; justify-content: center; flex-wrap: wrap; position: relative; }
.ab-btn-primary {
  display: inline-flex; align-items: center; gap: 7px;
  background: var(--ab-blue); color: #fff; padding: 0.75rem 1.8rem;
  border-radius: 10px; font-weight: 700; font-size: 0.95rem;
  text-decoration: none; border: none; cursor: pointer;
  transition: background 0.2s, transform 0.2s;
  box-shadow: 0 6px 20px rgba(28,115,186,0.4);
}
.ab-btn-primary:hover { background: #155fa0; transform: translateY(-2px); }
.ab-btn-ghost {
  display: inline-flex; align-items: center; gap: 7px;
  background: rgba(255,255,255,0.08); color: #fff; padding: 0.75rem 1.8rem;
  border-radius: 10px; font-weight: 600; font-size: 0.95rem;
  text-decoration: none; border: 1px solid rgba(255,255,255,0.2);
  transition: background 0.2s;
}
.ab-btn-ghost:hover { background: rgba(255,255,255,0.14); }

/* ── FOOTER ── */
.ab-footer {
  background: #04091f; padding: 36px 6%;
  text-align: center; font-size: 13px; color: rgba(255,255,255,0.35);
  font-family: 'DM Sans', sans-serif;
}
.ab-footer a { color: var(--ab-blue); text-decoration: none; }

/* ── REVEAL ── */
.ab-r { opacity: 0; transform: translateY(24px); transition: opacity 0.7s ease, transform 0.7s ease; }
.ab-r.vis { opacity: 1; transform: translateY(0); }

/* ── RESPONSIVE ── */
@media (max-width: 1024px) {
  .ab-why-grid { grid-template-columns: 1fr 1fr; }
  .ab-tech-grid { grid-template-columns: 1fr 1fr; }
}
@media (max-width: 768px) {
  .ab-hero { padding: 72px 5% 60px; }
  .ab-founder-inner { grid-template-columns: 1fr; gap: 48px; }
  .ab-story-inner { grid-template-columns: 1fr; gap: 40px; }
  .ab-ward-grid { grid-template-columns: 1fr; gap: 40px; }
  .ab-why-grid { grid-template-columns: 1fr; }
  .ab-tech-grid { grid-template-columns: 1fr 1fr; }
  .ab-section { padding: 60px 5%; }
}
`;

function useReveal() {
  useEffect(() => {
    const els = document.querySelectorAll('.ab-r');
    const io = new IntersectionObserver(
      es => es.forEach(e => { if (e.isIntersecting) e.target.classList.add('vis'); }),
      { threshold: 0.12 }
    );
    els.forEach(el => io.observe(el));
    return () => io.disconnect();
  }, []);
}

export default function About() {
  useReveal();

  const WHY = [
    { icon:'🇳🇵', title:"Nepal's First NFC Company",    desc:"Founded as the first NFC smart product company in Nepal — bringing contactless technology to a market that needed it." },
    { icon:'🏆', title:'Prime Minister Award Recipient', desc:'Recognised by the Prime Minister of Nepal with the National Talent Award for innovation in digital technology and NFC hardware.' },
    { icon:'🔧', title:'In-House Hardware',              desc:'We design and 3D-print our own hardware accessories. Cards, keyrings, rings, stands — all built and tested by our own team.' },
    { icon:'🏫', title:'Education-First Mission',        desc:'Our first large-scale deployment: smart ID cards for 1,700 students and teachers across 7 community schools in Pokhara.' },
    { icon:'🌐', title:'Full Ecosystem',                 desc:'From NFC tag to digital profile to analytics dashboard — Flap is the only platform that connects all of it in one place.' },
    { icon:'🤝', title:'Local Team, Local Support',      desc:'No outsourcing. Our engineering, hardware, and support teams are all based in Nepal. We fix problems same day.' },
  ];

  const TIMELINE = [
    { time:'2023', icon:'💡', title:'Idea & First Prototype',         desc:'Bikalpa Dhungana built the first Flap NFC card prototype in Kathmandu. Shared a profile by tapping a handmade card on his phone.' },
    { time:'2024 early', icon:'🏗️', title:'Product & Platform Build', desc:'Full profile system built: links, socials, payment wallets, analytics, messaging. First batch of physical NFC cards ordered.' },
    { time:'2024', icon:'🏆', title:'Prime Minister National Talent Award', desc:'Awarded the National Talent Award by the Prime Minister of Nepal for innovation in NFC technology and digital identity.' },
    { time:'2025 Q1', icon:'🏫', title:'Ward 26 Smart School Proposal', desc:'Submitted formal proposal to Pokhara Metropolitan City Ward No. 26 to deploy smart attendance & identity system in 7 community schools.' },
    { time:'May 2025', icon:'🚀', title:'Flap ID for 1,700 Students',  desc:'Deployment begins: UHF RFID gate readers installed at school entrances; 1,700 smart NFC+RFID ID cards printed and distributed.' },
    { time:'2025+', icon:'🌏', title:'National Expansion',             desc:'Expanding the Flap ecosystem: vehicle tags, home tags, pet tags, business cards — across Nepal and globally via flaap.me.' },
  ];

  const TECH = [
    { icon:'📡', name:'NFC / NTAG215', desc:'ISO 18000-6C · tap on any Android or iPhone' },
    { icon:'📻', name:'UHF RFID',      desc:'860–960 MHz EPC Gen2 · 1–5m hands-free detection' },
    { icon:'🖥️', name:'Node.js + MongoDB', desc:'Scalable backend built for real-time profile delivery' },
    { icon:'⚛️', name:'React',         desc:'Fast, responsive frontend with progressive web features' },
    { icon:'📱', name:'WhatsApp + SMS',desc:'Automated parent notifications via WhatsApp Business API' },
    { icon:'🖨️', name:'3D Printing',   desc:'In-house hardware prototyping and custom accessories' },
    { icon:'🔒', name:'Privacy-First', desc:'User-controlled visibility on every profile field' },
    { icon:'📊', name:'Live Analytics',desc:'Real-time tap, view, and click tracking per flapid' },
  ];

  return (
    <div className="ab-root">
      <style>{CSS}</style>
      {/* Shared Navbar from App.js renders above this */}

      {/* ── HERO ── */}
      <section className="ab-hero">
        <div className="ab-hero-inner">
          <div className="ab-eyebrow">
            <span>🇳🇵</span> Flap Technology · Pokhara & Kathmandu, Nepal
          </div>
          <h1>
            We're making Nepal<br/>
            <em>tap smarter</em><br/>
            <strong>with NFC.</strong>
          </h1>
          <p className="ab-hero-lead">
            Flap is Nepal's first NFC smart product company — building the digital
            identity layer for people, schools, vehicles, and businesses
            across the country.
          </p>
          <div className="ab-hero-award">
            🏆 Prime Minister National Talent Award · Innovation in NFC Technology
          </div>
        </div>
      </section>

      {/* ── FOUNDER ── */}
      <section className="ab-founder-section">
        <div className="ab-founder-inner">
          <div className="ab-r">
            <div className="ab-founder-avatar">B</div>
            <div className="ab-founder-name">Bikalpa Dhungana</div>
            <div className="ab-founder-role">Founder & CEO, Flap Technology</div>
            <p className="ab-founder-bio">
              Bikalpa built the first Flap NFC card prototype in Kathmandu in 2023 — just
              one card and a phone, but a proof that Nepal could leapfrog paper completely.
              What started as a digital business card became a full ecosystem: smart school
              identity systems, vehicle tags, pet tags, and a live analytics platform
              used across Nepal.
            </p>
            <p className="ab-founder-bio" style={{ marginTop: -8 }}>
              He was awarded the <strong>Prime Minister National Talent Award</strong> for
              his work in NFC hardware innovation — the first such recognition for
              contactless technology in Nepal.
            </p>
            <div className="ab-contact-row">
              <a href="mailto:bikalpa@flap.com.np" className="ab-contact-link">
                ✉️ bikalpa@flap.com.np
              </a>
              <a href="https://flap.com.np" className="ab-contact-link" target="_blank" rel="noreferrer">
                🌐 flap.com.np
              </a>
              <a href="https://bikalpadhungana.com.np" className="ab-contact-link" target="_blank" rel="noreferrer">
                👤 bikalpadhungana.com.np
              </a>
            </div>
          </div>

          <div className="ab-founder-visual ab-r" style={{ transitionDelay:'80ms' }}>
            {[
              { icon:'🏆', title:'Prime Minister National Talent Award',  desc:'Awarded by the Government of Nepal for outstanding innovation in NFC hardware and digital identity technology.' },
              { icon:'🇳🇵', title:"Nepal's First NFC Smart Product Company", desc:'Flap was founded as the first company in Nepal to design, manufacture, and deploy NFC-based smart products at scale.' },
              { icon:'🏫', title:'Ward 26 Smart School System',           desc:'Formal proposal accepted by Pokhara Metropolitan City Ward No. 26 to deploy smart attendance for 7 community schools.' },
              { icon:'🔧', title:'In-House 3D Printing',                   desc:'Custom NFC accessories are designed and prototyped in our own workshop — no outsourcing, full quality control.' },
            ].map(a => (
              <div className="ab-award-card" key={a.title}>
                <div className="ab-award-icon">{a.icon}</div>
                <div>
                  <div className="ab-award-title">{a.title}</div>
                  <div className="ab-award-desc">{a.desc}</div>
                </div>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* ── STORY ── */}
      <section className="ab-story-section">
        <div className="ab-story-inner">
          <div className="ab-r">
            <span className="ab-label">Our Story</span>
            <h2 className="ab-h2">From one card<br/><em>to a national platform</em></h2>
          </div>
          <div className="ab-story-prose ab-r" style={{ transitionDelay:'60ms' }}>
            <p>
              Nepal's professional networking was stuck in paper. Business cards got lost,
              numbers changed, and there was no way to update what you'd already handed out.
              Bikalpa built Flap to fix that — one NFC card that opens your entire digital
              profile on any phone, instantly, with no app needed.
            </p>
            <p>
              The first product was simple: a card. Then came keyrings, rings, wallet tags,
              and NFC stands for desks and reception counters. Each accessory linked to the
              same live Flap profile — meaning you update once, and every card you've ever
              tapped reflects the change in real time.
            </p>
            <p>
              The breakthrough moment came when Pokhara's Ward No. 26 asked whether Flap
              could solve student attendance. The answer was yes — but bigger than just
              attendance. We proposed a <strong>full smart identity system</strong>: UHF RFID
              gate readers for hands-free entry/exit detection across 7 community schools,
              combined with NFC smart ID cards that open each student's Flap profile.
            </p>
            <p>
              1,700 students and teachers. 7 schools. WhatsApp and SMS notifications sent to
              parents the moment their child arrives or leaves. This is what Flap is built for —
              not just business cards, but <strong>digital identity infrastructure for Nepal</strong>.
            </p>
          </div>
        </div>
      </section>

      {/* ── WARD 26 PROJECT DETAIL ── */}
      <section className="ab-ward-section">
        <div className="ab-ward-inner">
          <div className="ab-ward-grid">
            <div className="ab-ward-text">
              <span className="ab-label">Live Deployment</span>
              <h2 className="ab-h2">Smart schools in<br/><em>Pokhara Ward No. 26</em></h2>
              <p className="ab-lead">
                Nepal's first smart attendance and identity management system — deployed
                across 7 community schools under Pokhara Metropolitan City Ward No. 26.
                Fiscal Year 2082/83 (2025).
              </p>
              <div className="ab-ward-features">
                {[
                  { icon:'📡', text:'UHF RFID gate readers detect cards from 1–5 metres — no manual tap, no queues at the gate.' },
                  { icon:'💳', text:'Each student and teacher gets one NFC+RFID smart ID card. UHF for attendance, NFC for digital profile.' },
                  { icon:'💬', text:'Parents receive instant WhatsApp (primary) and SMS (backup) notifications on school entry and exit.' },
                  { icon:'📱', text:'Tap any card on a smartphone: name, class, school, parent contact, emergency info — all in under a second.' },
                  { icon:'🔒', text:'Emergency contact info is accessible to anyone who taps the card — critical for lost children or accidents.' },
                ].map((f,i) => (
                  <div className="ab-ward-feature" key={i}>
                    <span className="ab-ward-feature-icon">{f.icon}</span>
                    <span>{f.text}</span>
                  </div>
                ))}
              </div>
            </div>

            <div>
              <div className="ab-ward-stats">
                {[
                  { num:'7',     label:'Community Schools' },
                  { num:'1,700', label:'Students & Teachers' },
                  { num:'5m',    label:'RFID Detection Range' },
                  { num:'IP65',  label:'Weather Resistance' },
                  { num:'< 1s',  label:'NFC Profile Open Time' },
                  { num:'1 yr',  label:'Hardware Warranty' },
                ].map(s => (
                  <div className="ab-ward-stat" key={s.label}>
                    <div className="ab-ward-stat-num">{s.num}</div>
                    <div className="ab-ward-stat-label">{s.label}</div>
                  </div>
                ))}
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* ── WHY FLAP ── */}
      <section className="ab-why-section">
        <div className="ab-why-inner">
          <div className="ab-r">
            <span className="ab-label">Why choose Flap</span>
            <h2 className="ab-h2">Built different,<br/><em>by design</em></h2>
          </div>
          <div className="ab-why-grid">
            {WHY.map((w,i) => (
              <div className="ab-why-card ab-r" key={w.title} style={{ transitionDelay:`${i*60}ms` }}>
                <div className="ab-why-icon">{w.icon}</div>
                <div className="ab-why-title">{w.title}</div>
                <div className="ab-why-desc">{w.desc}</div>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* ── TIMELINE ── */}
      <section className="ab-timeline-section">
        <div className="ab-timeline-inner">
          <div className="ab-r">
            <span className="ab-label">Journey</span>
            <h2 className="ab-h2">How we got here</h2>
          </div>
          <div className="ab-timeline">
            {TIMELINE.map((t,i) => (
              <div className="ab-tl-item ab-r" key={t.title} style={{ transitionDelay:`${i*70}ms` }}>
                <div className="ab-tl-dot">{t.icon}</div>
                <div className="ab-tl-content">
                  <div className="ab-tl-time">{t.time}</div>
                  <div className="ab-tl-title">{t.title}</div>
                  <div className="ab-tl-desc">{t.desc}</div>
                </div>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* ── TECH ── */}
      <section className="ab-tech-section">
        <div className="ab-tech-inner">
          <div className="ab-r">
            <span className="ab-label">Technology</span>
            <h2 className="ab-h2">What powers Flap</h2>
          </div>
          <div className="ab-tech-grid">
            {TECH.map((t,i) => (
              <div className="ab-tech-item ab-r" key={t.name} style={{ transitionDelay:`${i*50}ms` }}>
                <div className="ab-tech-icon">{t.icon}</div>
                <div className="ab-tech-name">{t.name}</div>
                <div className="ab-tech-desc">{t.desc}</div>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* ── CTA ── */}
      <section className="ab-cta">
        <h2>Ready to go paperless?</h2>
        <p>Join the Flap ecosystem — cards, schools, vehicles, and more.</p>
        <div className="ab-cta-btns">
          <Link to="/register" className="ab-btn-primary">Create your free Flap →</Link>
          <a href="mailto:card@flap.com.np" className="ab-btn-ghost">Contact the team</a>
        </div>
      </section>

      {/* ── FOOTER ── */}
      <footer className="ab-footer">
        <div>
          © {new Date().getFullYear()} Flap Technology · Pokhara & Kathmandu, Nepal ·{' '}
          <a href="mailto:card@flap.com.np">card@flap.com.np</a>{' '}·{' '}
          <Link to="/">flap.com.np</Link>
        </div>
      </footer>
    </div>
  );
}